import { useState, FC, useRef, RefObject } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions, Paper, IconButton, Box, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { GroupBalanceCreate } from 'repository/timekeeper/@types/GroupBalance';
import { GroupBalanceForm } from './GroupBalanceForm';
import { GroupBalance } from 'repository/timekeeper/@types/GroupBalance';


interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: GroupBalance[];
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

export const CreateGroupBalance: FC<CreateProps> = ({ open, setOpen, rows, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<GroupBalanceCreate>({
        name: '',
        description: null,
        balances: []
    });

    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false);

    const changes = () => {
        if (
            data.name !== '' || (data.balances && data.balances.length > 0)
        ) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        setData({
            name: '',
            description: null,
            balances: []
        });
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const handleFormChange = (newData: { name: string; description?: string | null; balances?: number[] }) => {
        setData({
            name: newData.name,
            description: newData.description,
            balances: newData.balances || []
        });
    };

    const Create = async () => {
        setLoading(true);

        try {
            // Crear todos los registros
            console.log('Code to create:', data);
            const createdBalanceDomino = await timekeeperService.group_balance.create(data);
            console.log('Created Balance Domino:', createdBalanceDomino);

            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.balance_domino.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <GroupBalanceForm
                    initialData={{
                        name: data.name,
                        description: data.description,
                        balances: data.balances || []
                    }}
                    onChange={handleFormChange}
                    rows={rows}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={() => Create()} loading={loading} disabled={!data.balances || data.balances.length === 0}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};